<template>
    <div class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
        <SelectInput
            class="sm:col-span-2"
            label="Font family"
            placeholder="Editor default"
            :data="fontOptionsWithSaved"
            :model-value="modelValue['font-family'] ?? ''"
            :disabled="disabled"
            :error="errorFor('font-family')"
            unselectable
            search
            @update:modelValue="set('font-family', $event)"
        />
        <TextInput
            label="Font size"
            placeholder="e.g. 16px"
            :model-value="modelValue['font-size'] ?? ''"
            :disabled="disabled"
            :error="errorFor('font-size')"
            @update:modelValue="set('font-size', $event)"
        />
        <SelectInput
            label="Font weight"
            placeholder="Editor default"
            :data="fontWeights"
            :model-value="modelValue['font-weight'] ?? ''"
            :disabled="disabled"
            :error="errorFor('font-weight')"
            unselectable
            @update:modelValue="set('font-weight', $event)"
        />
        <SelectInput
            label="Font style"
            placeholder="Editor default"
            :data="fontStyles"
            :model-value="modelValue['font-style'] ?? ''"
            :disabled="disabled"
            :error="errorFor('font-style')"
            unselectable
            @update:modelValue="set('font-style', $event)"
        />
        <TextInput
            label="Letter spacing"
            placeholder="e.g. 0.5px"
            :model-value="modelValue['letter-spacing'] ?? ''"
            :disabled="disabled"
            :error="errorFor('letter-spacing')"
            @update:modelValue="set('letter-spacing', $event)"
        />
        <SelectInput
            label="Text transform"
            placeholder="Editor default"
            :data="textTransforms"
            :model-value="modelValue['text-transform'] ?? ''"
            :disabled="disabled"
            :error="errorFor('text-transform')"
            unselectable
            @update:modelValue="set('text-transform', $event)"
        />
        <SelectInput
            label="Text decoration"
            placeholder="Editor default"
            :data="textDecorations"
            :model-value="modelValue['text-decoration'] ?? ''"
            :disabled="disabled"
            :error="errorFor('text-decoration')"
            unselectable
            @update:modelValue="set('text-decoration', $event)"
        />
        <ColorField
            label="Text color"
            :model-value="modelValue.color ?? ''"
            :disabled="disabled"
            :error="errorFor('color')"
            @update:modelValue="set('color', $event)"
        />
        <ColorField
            v-if="withBackground"
            label="Background color"
            :model-value="modelValue['background-color'] ?? ''"
            :disabled="disabled"
            :error="errorFor('background-color')"
            @update:modelValue="set('background-color', $event)"
        />
        <TextInput
            v-if="withRadius"
            label="Corner radius"
            placeholder="e.g. 4px"
            :model-value="modelValue['border-radius'] ?? ''"
            :disabled="disabled"
            :error="errorFor('border-radius')"
            @update:modelValue="set('border-radius', $event)"
        />
    </div>
</template>

<script lang="ts" setup>
import { PropType } from 'vue';
import ISelectable from '@/types/Atomic/ISelectable';
import { ITypography } from '@/types/Settings/IDefaultTemplateSettings';
import ColorField from './ColorField.vue';

type Fields = ITypography & { 'border-radius'?: string };

const props = defineProps({
    modelValue: {
        type: Object as PropType<Fields>,
        required: true,
    },
    fontOptions: {
        type: Array as PropType<ISelectable[]>,
        required: true,
    },
    /** Field name prefix of this section's validation errors, e.g. `settings.h1`. */
    errorPrefix: {
        type: String,
        required: true,
    },
    errors: {
        type: Object as PropType<Record<string, string>>,
        default: () => ({}),
    },
    withBackground: {
        type: Boolean,
        default: true,
    },
    withRadius: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

const set = (key: keyof Fields, value: string | number | null) => {
    emit('update:modelValue', {
        ...props.modelValue,
        [key]: value === null ? '' : String(value),
    });
};

/**
 * A saved font the team has since disabled or removed is still applied, so it
 * stays listed instead of leaving the field looking empty.
 */
const fontOptionsWithSaved = computed<ISelectable[]>(() => {
    const saved = props.modelValue['font-family'];

    if (!saved || props.fontOptions.some((option) => option.value === saved)) {
        return props.fontOptions;
    }

    return [
        ...props.fontOptions,
        {
            title: `${saved.split(',')[0].replace(/['"]/g, '').trim()} (not in your fonts)`,
            value: saved,
        },
    ];
});

const errorFor = (key: keyof Fields) =>
    props.errors[`${props.errorPrefix}.${key}`] ?? '';

const options = (values: string[]): ISelectable[] =>
    values.map((value) => ({
        title: value.charAt(0).toUpperCase() + value.slice(1),
        value,
    }));

const fontWeights: ISelectable[] = [
    { title: 'Normal', value: 'normal' },
    { title: 'Bold', value: 'bold' },
    ...['100', '200', '300', '400', '500', '600', '700', '800', '900'].map(
        (value) => ({ title: value, value })
    ),
];
const fontStyles = options(['normal', 'italic']);
const textTransforms = options(['none', 'uppercase', 'lowercase', 'capitalize']);
const textDecorations = options(['none', 'underline', 'line-through']);
</script>
